import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getDimensionScore, getOverallScore } from '../utils/scoring';
import { deriveRecommendedActions, getMissingRecommendedActions } from '../utils/actionHelpers';

const GovernanceContext = createContext(null);

export function GovernanceProvider({ children }) {
  const [clients, setClients] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [actions, setActions] = useState([]);
  const [guideDeployments, setGuideDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadAll() {
      try {
        const [
          { data: c, error: ce },
          { data: a, error: ae },
          { data: ac, error: ace },
          { data: gd, error: gde },
        ] = await Promise.all([
            supabase.from('clients').select('*').order('created_at', { ascending: false }),
            supabase.from('assessments').select('*'),
            supabase.from('actions').select('*').order('created_at', { ascending: true }),
            supabase.from('guide_deployments').select('*').order('created_at', { ascending: true }),
          ]);
        if (ce) throw ce;
        if (ae) throw ae;
        if (ace) throw ace;
        if (gde) throw gde;
        setClients(c || []);
        setAssessments(a || []);
        setActions(ac || []);
        setGuideDeployments(gd || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  // ── Clients ───────────────────────────────────────────────────────────────
  const addClient = useCallback(async (fields) => {
    const { data, error } = await supabase
      .from('clients')
      .insert(fields)
      .select()
      .single();
    if (error) throw error;
    setClients((prev) => [data, ...prev]);
    return data;
  }, []);

  const updateClient = useCallback(async (id, fields) => {
    const { data, error } = await supabase
      .from('clients')
      .update(fields)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    setClients((prev) => prev.map((c) => (c.id === id ? data : c)));
    return data;
  }, []);

  const deleteClient = useCallback(async (id) => {
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) throw error;
    setClients((prev) => prev.filter((c) => c.id !== id));
    setAssessments((prev) => prev.filter((a) => a.client_id !== id));
    setActions((prev) => prev.filter((a) => a.client_id !== id));
    setGuideDeployments((prev) => prev.filter((g) => g.client_id !== id));
  }, []);

  // ── Assessments ───────────────────────────────────────────────────────────
  const upsertAssessment = useCallback(
    async (clientId, dim, questionId, score, notes) => {
      // Build the optimistic updated assessments list used immediately for sync
      let updatedAssessments;
      setAssessments((prev) => {
        const idx = prev.findIndex(
          (a) => a.client_id === clientId && a.dimension === dim && a.question_id === questionId
        );
        const tempRecord = { client_id: clientId, dimension: dim, question_id: questionId, score, notes };
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...tempRecord };
          updatedAssessments = updated;
          return updated;
        }
        const next = [...prev, { id: `temp-${Date.now()}`, ...tempRecord }];
        updatedAssessments = next;
        return next;
      });

      const { data, error } = await supabase
        .from('assessments')
        .upsert(
          { client_id: clientId, dimension: dim, question_id: questionId, score, notes, updated_at: new Date().toISOString() },
          { onConflict: 'client_id,dimension,question_id' }
        )
        .select()
        .single();
      if (error) throw error;

      // Replace temp record with real data
      setAssessments((prev) => {
        const idx = prev.findIndex(
          (a) => a.client_id === clientId && a.dimension === dim && a.question_id === questionId
        );
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = data;
          return updated;
        }
        return prev;
      });

      // Sync recommended actions using the fresh assessment snapshot
      await syncRecommendedActionsFromSnapshot(clientId, dim, updatedAssessments);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions]
  );

  // Internal helper that takes an explicit assessments snapshot so it never reads stale state
  const syncRecommendedActionsFromSnapshot = useCallback(
    async (clientId, dim, freshAssessments) => {
      const currentActions = actions;
      const derived = deriveRecommendedActions(freshAssessments, clientId, dim);
      const missing = getMissingRecommendedActions(currentActions, derived, clientId, dim);
      if (missing.length === 0) return;
      const { data, error } = await supabase.from('actions').insert(missing).select();
      if (error) { console.error('syncRecommendedActions error', error); return; }
      if (data) setActions((prev) => [...prev, ...data]);
    },
    [actions]
  );

  const syncRecommendedActions = useCallback(
    async (clientId, dim) => {
      await syncRecommendedActionsFromSnapshot(clientId, dim, assessments);
    },
    [assessments, syncRecommendedActionsFromSnapshot]
  );

  // ── Actions ───────────────────────────────────────────────────────────────
  const addAction = useCallback(async (fields) => {
    const { data, error } = await supabase
      .from('actions')
      .insert({ ...fields, is_custom: true })
      .select()
      .single();
    if (error) throw error;
    setActions((prev) => [...prev, data]);
    return data;
  }, []);

  const updateAction = useCallback(async (id, fields) => {
    const { data, error } = await supabase
      .from('actions')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    setActions((prev) => prev.map((a) => (a.id === id ? data : a)));
    return data;
  }, []);

  const deleteAction = useCallback(async (id) => {
    const { error } = await supabase.from('actions').delete().eq('id', id);
    if (error) throw error;
    setActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // ── Guide Deployments ─────────────────────────────────────────────────────
  const deployGuide = useCallback(async (clientId, slug, notes = '') => {
    const { data, error } = await supabase
      .from('guide_deployments')
      .insert({ client_id: clientId, guide_slug: slug, status: 'pending', notes })
      .select()
      .single();
    if (error) throw error;
    setGuideDeployments((prev) => [...prev, data]);
    return data;
  }, []);

  const updateGuideDeployment = useCallback(async (id, fields) => {
    const { data, error } = await supabase
      .from('guide_deployments')
      .update(fields)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    setGuideDeployments((prev) => prev.map((g) => (g.id === id ? data : g)));
    return data;
  }, []);

  const removeGuideDeployment = useCallback(async (id) => {
    const { error } = await supabase.from('guide_deployments').delete().eq('id', id);
    if (error) throw error;
    setGuideDeployments((prev) => prev.filter((g) => g.id !== id));
  }, []);

  const getClientDeployments = useCallback(
    (clientId) => guideDeployments.filter((g) => g.client_id === clientId),
    [guideDeployments]
  );

  // ── Selectors ─────────────────────────────────────────────────────────────
  const getClientAssessments = useCallback(
    (clientId) => assessments.filter((a) => a.client_id === clientId),
    [assessments]
  );

  const getClientActions = useCallback(
    (clientId) => actions.filter((a) => a.client_id === clientId),
    [actions]
  );

  const getClientDimensionScore = useCallback(
    (clientId, dim) => {
      const clientAssessments = assessments.filter((a) => a.client_id === clientId);
      return getDimensionScore(clientAssessments, dim);
    },
    [assessments]
  );

  const getClientOverallScore = useCallback(
    (clientId) => {
      const clientAssessments = assessments.filter((a) => a.client_id === clientId);
      return getOverallScore(clientAssessments);
    },
    [assessments]
  );

  return (
    <GovernanceContext.Provider
      value={{
        clients,
        assessments,
        actions,
        guideDeployments,
        loading,
        error,
        addClient,
        updateClient,
        deleteClient,
        upsertAssessment,
        syncRecommendedActions,
        addAction,
        updateAction,
        deleteAction,
        deployGuide,
        updateGuideDeployment,
        removeGuideDeployment,
        getClientDeployments,
        getClientAssessments,
        getClientActions,
        getClientDimensionScore,
        getClientOverallScore,
      }}
    >
      {children}
    </GovernanceContext.Provider>
  );
}

export function useGovernance() {
  const ctx = useContext(GovernanceContext);
  if (!ctx) throw new Error('useGovernance must be used within GovernanceProvider');
  return ctx;
}
